import type { AccessRuleChangeRequest } from "../requests/accessRuleRequestBuilder";
import type {
  LiftAccessRuleBatchSaveResult,
  LiftAccessRuleSaveResult,
} from "../../models";
import { isRetryableSaveError } from "../saveErrorCodes";
import { entitlementContextKey } from "./models";
import type {
  BulkFailureIdentity,
  BulkSaveFailure,
} from "./BulkSaveFailuresView";

/** A bulk edit save and everything learned about it across the first attempt and any retries. */
export type BulkSaveRun = {
  requests: AccessRuleChangeRequest[];
  /** Display columns per rule set identity, for the outcome grid. */
  identities: Map<string, BulkFailureIdentity>;
  /**
   * Latest outcome per rule set. Keyed, not appended: a retry only reports on what it
   * re-sent, so counts are recalculated per rule set rather than accumulated.
   */
  outcomes: Map<string, LiftAccessRuleSaveResult>;
  /** Requests no attempt has reached — the run stopped before them. */
  notAttempted: number;
};

export const mergeSaveOutcomes = (
  previous: Map<string, LiftAccessRuleSaveResult>,
  result: LiftAccessRuleBatchSaveResult | undefined,
): Map<string, LiftAccessRuleSaveResult> => {
  const merged = new Map(previous);
  for (const saveResult of result?.results ?? []) {
    // Shouldn't happen — the runner keys results on the contexts it sent. Dropping
    // one leaves that rule set counted as not attempted.
    if (!saveResult.context) continue;
    merged.set(entitlementContextKey(saveResult.context), saveResult);
  }
  return merged;
};

/** Requests the run has no outcome for, so they were never attempted. */
export const notAttemptedCount = (
  requests: AccessRuleChangeRequest[],
  outcomes: Map<string, LiftAccessRuleSaveResult>,
): number =>
  requests.filter(
    (request) => !outcomes.has(entitlementContextKey(request.Context)),
  ).length;

/** Failed rule sets as the outcome grid shows them. */
export const toSaveFailures = (run: BulkSaveRun): BulkSaveFailure[] =>
  [...run.outcomes.entries()]
    .filter(([, result]) => !result.success)
    .map(([key, result]): BulkSaveFailure | undefined => {
      const ruleSet = run.identities.get(key);
      // Dropped rather than rendered with blank identifying columns.
      if (!ruleSet) return undefined;
      return {
        ruleSet,
        errorStatus: result.errorStatus,
        errorCode: result.errorCode,
        errorMessage: result.errorMessage,
      };
    })
    .filter((failure): failure is BulkSaveFailure => !!failure);

/**
 * Everything worth sending again: the transient failures, plus anything no attempt
 * has reached. Both populations matter — the breaker stops the run on its first
 * never-sent request, so most of a large run can end up unattempted rather than
 * failed, and retrying only the failure would strand the rest.
 */
export const retryableSaveRequests = (
  run: BulkSaveRun,
): AccessRuleChangeRequest[] => {
  const retryableKeys = new Set(
    [...run.outcomes.entries()]
      .filter(
        ([key, result]) =>
          !result.success &&
          isRetryableSaveError(result) &&
          run.identities.has(key),
      )
      .map(([key]) => key),
  );

  return run.requests.filter((request) => {
    const key = entitlementContextKey(request.Context);
    // No outcome at all = never attempted, so nothing rules it out.
    return retryableKeys.has(key) || !run.outcomes.has(key);
  });
};

export const successCount = (run: BulkSaveRun): number =>
  [...run.outcomes.values()].filter((outcome) => outcome.success).length;

export const failureCount = (run: BulkSaveRun): number =>
  [...run.outcomes.values()].filter((outcome) => !outcome.success).length;
